<template>
    <div>
        <div class="form-group" :class="{ 'mb-4': !removeMargin }">
            <label :for="compCustomerSelect" v-if="noLabel === false">
                <strong>
                    {{ title }}
                    <span v-if="required" class="text-danger">*</span>
                </strong>
            </label>

            <v-select
                v-model="model"
                :options="customers"
                :clearable="!isDisabled"
                placeholder="Select a Customer"
                label="name"
                :searchable="searchable"
                @search="fetchOptions"
                @clear="() => fetchCustomers()"
                :disabled="isDisabled"
                :id="compCustomerSelect"
            ></v-select>

            <!-- This select is used in blade files in forms as well as Vue components -->
            <!-- This is why it's also passed out as a hidden input -->
            <input type="hidden" name="customer_id" id="customer_id" :value="model?.id ?? ''">

            <div v-if="errors && errors.length > 0" >
                <div class="alert alert-danger mt-2" role="alert" v-for="(error, index) in errors" :key="index">
                    {{ error }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, useId, watch } from 'vue';
import VSelect from 'vue-select';
import axios from 'axios';
import debounce from 'lodash/debounce';

const props = defineProps({
    title: {
        type: String,
        default: 'Customer'
    },
    old: {
        type: String
    },
    errors: {
        type: Array,
        default: () => []
    },
    customer: {
        type: Object,
        default: null
    },
    passedCustomers: {
        type: Array,
        default: () => []
    },
    disableWhenPassedCustomer: {
        type: Boolean,
        default: false
    },
    required: {
        type: Boolean,
        default: false
    },
    removeMargin: {
        type: Boolean,
        default: false
    },
    ajaxUrl: {
        type: String
    },
    searchable: {
        type: Boolean,
        default: true
    },
    noLabel: {
        type: Boolean,
        default: false
    },
});

const model = defineModel({
    type: Object,
    default: null,
});

const customers = ref(props.passedCustomers);
const isDisabled = ref(false);

const compCustomerSelect = useId();

// if a URL is provided, use it to fetch customers, otherwise use the default route
function generateAllCustomersRoute() {
    if (props.ajaxUrl) {
        return props.ajaxUrl;
    }
    return route('api.movements.customers.select');
}

const fetchCustomers = async (loading) => {
    try {
        if (loading) loading(true);
        const { data } = await axios.get(generateAllCustomersRoute());
        customers.value = Array.isArray(data) ? data : Object.values(data);
    } catch (error) {
        console.log(error);
    } finally {
        if (loading) loading(false); // ← guard it
    }
};


// Only search on 3 characters or more
const fetchOptions = (search, loading) => {
    if (!search) {
        fetchCustomers(loading);
        return;
    }
    if (search.length > 2) {
        debounceFetchOptions(search, loading);
    }
};

const debounceFetchOptions = debounce(async function (search, loading) {
    try {
        if (loading) loading(true);
        const url = `${generateAllCustomersRoute()}?search=${encodeURIComponent(search)}`;
        const { data } = await axios.get(url);
        customers.value = Array.isArray(data) ? data : Object.values(data);
    } catch (error) {
        console.log(error);
    } finally {
        if (loading) loading(false);
    }
}, 500);


onMounted(async () => {
    try {
        await fetchCustomers();

        // Old value handling
        if (props.old != null && !isNaN(Number(props.old))) {
            const id = Number(props.old);
            const found = customers.value.find(c => c.id === id);
            if (found) model.value = found;
        }

        // Preselected customer handling
        if (props.customer) {
            const url = `${generateAllCustomersRoute()}?search=${encodeURIComponent(props.customer.name)}`;
            const { data } = await axios.get(url);
            customers.value = Array.isArray(data) ? data : Object.values(data);
            model.value = customers.value.find(c => c.id === props.customer.id) || model.value;
            isDisabled.value = true;
        }
    } catch (error) {
        console.error('Error loading customers:', error);
    }
});

watch(model, (newValue) => {
    if (newValue === null) {
        fetchCustomers();
    }
});

</script>
